import { ReactElement, useMemo, memo } from 'react';
import { Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVectorSquare as LogisticsZonesIcon } from '@fortawesome/free-solid-svg-icons';

import {
  LogisiticZoneInfoWithTooltipProps,
  LogisiticZoneInfoProps,
} from './types';
import DescriptionWithIconBox from '../DescriptionWithIconBox';
import { DescriptionWithIconBoxText } from '../DescriptionWithIconBox/styles';

export const LogisiticZoneInfoWithTooltip = ({
  children,
  message,
}: LogisiticZoneInfoWithTooltipProps): ReactElement => {
  if (!message) return <>{children}</>;

  return (
    <Tooltip title={message} arrow placement="left">
      <span style={{ width: '100%' }}>{children}</span>
    </Tooltip>
  );
};

const LogisiticZoneInfo = ({
  name,
  description,
  color,
  fontSize = 14,
  fontWeight,
  withTooltip = false,
}: LogisiticZoneInfoProps): ReactElement => {
  const text = useMemo(
    () => (description ? `${name} (${description})` : name),
    [description, name],
  );

  const tooltipMessage = useMemo(
    () => (withTooltip ? text : undefined),
    [withTooltip, text],
  );

  if (!color)
    return (
      <LogisiticZoneInfoWithTooltip message={tooltipMessage}>
        <DescriptionWithIconBoxText
          variant="inherit"
          sx={{ fontSize }}
          fontWeight={fontWeight}
        >
          {text}
        </DescriptionWithIconBoxText>
      </LogisiticZoneInfoWithTooltip>
    );

  return (
    <LogisiticZoneInfoWithTooltip message={tooltipMessage}>
      <DescriptionWithIconBox
        icon={<FontAwesomeIcon icon={LogisticsZonesIcon} />}
        description={text}
        size={fontSize}
        iconColor={color}
        iconSize={18}
        fontWeight={fontWeight}
      />
    </LogisiticZoneInfoWithTooltip>
  );
};

export default memo(LogisiticZoneInfo);
